import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { TABLET_BIG } from 'Douleutaras/constants/media-queries';

import './MenuScrollableWrapper.scss';

export const MenuScrollableWrapper = ({ parentRef, headerHeight = 80, onHeightChange, children }) => {
  const containerRef = useRef(null);
  const slotRef = useRef(null);
  const isMobileResolution = useMediaQuery({ maxWidth: TABLET_BIG.MIN_WIDTH - 1 });
  const [isFixed, setIsFixed] = useState(false);
  const [offsetTop, setOffsetTop] = useState(0);
  // Viewport box of the slot, so the fixed menu spans its own content column instead of a
  // viewport percentage — which overlapped the aside once the aside got a fixed width.
  const [slotBox, setSlotBox] = useState({ left: 0, width: 0 });
  // Height the menu occupied before it went fixed, held by the outer element so the content
  // below does not jump up by that amount — which is what threw off the tab scroll offsets.
  const [reservedHeight, setReservedHeight] = useState(0);
  const scrollTimeoutRef = useRef(null);

  const handleScroll = () => {
    if (scrollTimeoutRef.current) {
      cancelAnimationFrame(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = requestAnimationFrame(() => {
      if (!parentRef.current || !containerRef.current || !slotRef.current) { return; }

      const parentRect = parentRef.current.getBoundingClientRect();
      const elementHeight = containerRef.current.clientHeight;
      const parentBottom = parentRect.bottom;

      setReservedHeight(elementHeight);

      const slotRect = slotRef.current.getBoundingClientRect();
      setSlotBox(box => (box.left === slotRect.left && box.width === slotRect.width
        ? box
        : { left: slotRect.left, width: slotRect.width }));

      const shouldFix = parentRect.top <= headerHeight && parentBottom > headerHeight + elementHeight;

      if (shouldFix) {
        setIsFixed(true);
        setOffsetTop(headerHeight);
      } else {
        if (parentRect.top > headerHeight) {
          setIsFixed(false);
          setOffsetTop(parentRect.top);
        } else {
          setIsFixed(false);
          setOffsetTop(parentBottom - elementHeight);
        }
      }
    });
  };

  // Published upward so the sections can offset their scroll target by the pinned menu's real
  // height instead of a guessed constant.
  useEffect(() => {
    onHeightChange?.(reservedHeight);
  }, [reservedHeight, onHeightChange]);

  useEffect(() => {
    if (isMobileResolution) { return; }

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll(); // Первоначальная проверка

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (scrollTimeoutRef.current) { cancelAnimationFrame(scrollTimeoutRef.current); }
    };
  }, [isMobileResolution]);

  return (
    <div ref={slotRef} styleName="fixed-menu-slot" style={{ minHeight: isFixed ? reservedHeight : undefined }}>
      <div
        ref={containerRef}
        styleName={`fixed-menu-section ${isFixed ? 'fixed' : 'static'}`}
        style={isMobileResolution
          ? { top: 'auto' }
          : { top: offsetTop, ...(isFixed ? slotBox : {}) }}
      >
        {children}
      </div>
    </div>
  );
};
